import { useState } from 'react';
import useSEO from '../hooks/useSEO';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // SEO metadata
  useSEO({
    title: 'Contact Us - Magnolia Mold Co',
    description: 'Get in touch with Magnolia Mold Co. Questions about our handcrafted earrings? Want to place a custom order? We\'d love to hear from you!',
    url: 'https://magnoliamoldco.com/contact',
    keywords: 'contact magnolia mold, custom earring orders, handmade jewelry inquiry'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        setError(data.error || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setError('Failed to send message. Please email us directly at magnoliamolds26@gmail.com');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div>
      {/* Page header */}
      <div className="bg-cream-50 border-b border-cream-300">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16 text-center">
          <p className="eyebrow mb-3">Get in Touch</p>
          <h1 className="section-title mb-4">Contact Us</h1>
          <div className="flourish mb-4" aria-hidden="true">✦</div>
          <p className="text-lg text-ink-soft">
            Questions about an order, a custom pair, or where we'll be next? We'd love to hear from you.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
          {/* Contact Form */}
          <div className="md:col-span-3 bg-white rounded-2xl border border-cream-300 p-6 md:p-10">
            <h2 className="font-serif text-3xl font-semibold text-ink mb-6">Send a message</h2>

            {submitted && (
              <div className="mb-6 p-4 bg-sage-50 border border-sage-200 rounded-lg text-sage-800">
                Thank you! We'll get back to you soon.
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-ink-soft mb-2">
                  Name
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="field"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-ink-soft mb-2">
                  Email
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="field"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-ink-soft mb-2">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="field"
                />
              </div>

              <button type="submit" disabled={loading} className="btn-brand w-full py-3.5">
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="md:col-span-2 space-y-8 md:pt-4">
            <ContactItem label="Email">
              <a href="mailto:magnoliamolds26@gmail.com" className="hover:text-sage-700 transition-colors break-all">
                magnoliamolds26@gmail.com
              </a>
            </ContactItem>

            <ContactItem label="Phone">
              <a href="tel:+12297330303" className="hover:text-sage-700 transition-colors">
                (229) 733-0303
              </a>
            </ContactItem>

            <ContactItem label="Follow Along">
              <div className="flex gap-5">
                <a href="https://www.facebook.com/profile.php?id=61586223724947" target="_blank" rel="noopener noreferrer" className="text-sage-700 hover:text-sage-800 underline underline-offset-4">
                  Facebook
                </a>
                <a href="https://www.instagram.com/magnoliamoldco" target="_blank" rel="noopener noreferrer" className="text-sage-700 hover:text-sage-800 underline underline-offset-4">
                  Instagram
                </a>
              </div>
            </ContactItem>

            <img src="/logo.png" alt="" className="w-40 opacity-80 hidden md:block" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactItem({ label, children }) {
  return (
    <div className="border-l-2 border-gold-400 pl-5">
      <h3 className="eyebrow mb-2">{label}</h3>
      <div className="text-lg text-ink">{children}</div>
    </div>
  );
}
